import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { navItems } from '@/mocks/homeData';
import { useLanguageContext } from '@/i18n/LanguageContext';
import { useTranslation } from 'react-i18next';

// Map original Japanese label to translation key used in common namespace
const NAV_KEY_MAP: Record<string, string> = {
  'ホーム': 'common.nav.home',
  'アカデミーについて': 'common.nav.about',
  'コース紹介': 'common.nav.courses',
  'カリキュラム': 'common.nav.curriculum',
  '料金・特典': 'common.nav.pricing',
  '入学案内': 'common.nav.admission',
  '法人研修': 'common.nav.corporate',
  'お問い合わせ': 'common.nav.contact',
};

const SUB_KEY_MAP: Record<string, string> = {
  'アカデミー概要': 'common.nav.about.overview',
  '教育理念・方針': 'common.nav.about.philosophy',
  '全日本執事協会について': 'common.nav.about.association',
  '施設・設備紹介': 'common.nav.about.facilities',
  'アクセス': 'common.nav.about.access',
  'コース一覧・比較': 'common.nav.courses.list',
  'エッセンシャルコース': 'common.nav.courses.essential',
  'アドバンスコース': 'common.nav.courses.advanced',
  'マスターコース': 'common.nav.courses.master',
  '年間スケジュール': 'common.nav.courses.schedule',
  'カリキュラム概要': 'common.nav.curriculum.overview',
  '実践トレーニング': 'common.nav.curriculum.training',
  '使用教材': 'common.nav.curriculum.materials',
  '習得スキル一覧': 'common.nav.curriculum.skills',
  '料金概要': 'common.nav.pricing.overview',
  'コース別料金詳細': 'common.nav.pricing.details',
  '受講特典': 'common.nav.pricing.benefits',
  '修了証について': 'common.nav.pricing.certificate',
  '入学案内概要': 'common.nav.admission.overview',
  '応募資格・申込の流れ': 'common.nav.admission.application',
  '申込フォーム': 'common.nav.admission.apply',
  '個別相談予約': 'common.nav.admission.consultation',
  '体験講座': 'common.nav.admission.seminar',
  'お問い合わせフォーム': 'common.nav.contact.form',
  'よくある質問': 'common.nav.contact.faq',
  '資料請求': 'common.nav.contact.brochure',
  '講師ブログ': 'common.nav.contact.blog',
  '法人研修について': 'common.nav.corporate.overview',
  '法人資料請求': 'common.nav.corporate.brochure',
};

// solid: 暗色ヒーローを持たないページ(news詳細等)で、初期表示から solid(scrolled時と同じ暗色)ヘッダーにする。
// 既定 false のため、暗色heroページ(トップ/一覧等)の従来挙動(スクロール前は透過)は不変。
export default function Header({ solid = false }: { solid?: boolean }) {
  const [scrolled, setScrolled] = useState(false);
  const [activeMenu, setActiveMenu] = useState<number | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedMobile, setExpandedMobile] = useState<number | null>(null);
  const location = useLocation();
  const menuTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { lang, altLangPath } = useLanguageContext();
  const { t } = useTranslation();

  // BUG-01: JP always points to ja path, EN always points to en path
  const jaPath = lang === 'ja' ? location.pathname : altLangPath(location.pathname);
  const enPath = lang === 'en' ? location.pathname : altLangPath(location.pathname);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveMenu(null);
    setExpandedMobile(null);
  }, [location.pathname]);

  const handleMenuEnter = (index: number) => {
    if (menuTimeout.current) clearTimeout(menuTimeout.current);
    setActiveMenu(index);
  };

  const handleMenuLeave = () => {
    menuTimeout.current = setTimeout(() => {
      setActiveMenu(null);
    }, 200);
  };

  const toggleMobileSub = (index: number) => {
    setExpandedMobile(expandedMobile === index ? null : index);
  };

  // Resolve nav link href for current language (prepend /en if lang===en)
  const navHref = (href: string) =>
    lang === 'en' ? (href === '/' ? '/en' : `/en${href}`) : href;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        (solid || scrolled || activeMenu !== null || mobileOpen)
          ? 'bg-primary-900/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      {/* Utility Bar */}
      <div
        className={`hidden md:block border-b transition-all duration-500 ${
          scrolled
            ? 'border-white/10'
            : 'border-white/15'
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16 flex justify-end items-center h-9">
          <div className="flex items-center gap-6 text-[13px] tracking-wider">
            <Link
              to={navHref('/brochure')}
              className="text-foreground-300 hover:text-accent-500 transition-colors duration-300 whitespace-nowrap"
            >
              {t('common.header.brochure')}
            </Link>
            <span className="text-foreground-500/40">|</span>
            <Link
              to={navHref('/news')}
              className="text-foreground-300 hover:text-accent-500 transition-colors duration-300 whitespace-nowrap"
            >
              {t('common.header.news')}
            </Link>
            <span className="text-foreground-500/40">|</span>
            <Link
              to={navHref('/sitemap')}
              className="text-foreground-300 hover:text-accent-500 transition-colors duration-300 whitespace-nowrap"
            >
              {t('common.header.sitemap')}
            </Link>
            <span className="text-foreground-500/40">|</span>
            {/* BUG-01 fix: JP link always goes to ja path, highlighted when lang===ja */}
            <Link
              to={jaPath}
              className={`transition-colors duration-300 whitespace-nowrap ${
                lang === 'ja' ? 'text-accent-400' : 'text-foreground-300 hover:text-accent-500'
              }`}
            >
              JP
            </Link>
            <span className="text-foreground-500/40">/</span>
            {/* BUG-01 fix: EN link always goes to en path, highlighted when lang===en */}
            <Link
              to={enPath}
              className={`transition-colors duration-300 whitespace-nowrap ${
                lang === 'en' ? 'text-accent-400' : 'text-foreground-300 hover:text-accent-500'
              }`}
            >
              EN
            </Link>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-[84px]">
          {/* Logo */}
          <Link to={navHref('/')} className="flex items-center gap-3 group">
            <img
              src="/images/brand/logo-tjba-2026.png"
              alt="日本執事アカデミー TJBA"
              className="h-14 w-14 object-contain shrink-0"
            />
            <span className="flex flex-col">
              <span className={`font-heading text-xl tracking-[0.08em] leading-tight transition-colors duration-300 ${
                scrolled ? 'text-foreground-50' : 'text-foreground-50'
              }`}>
                {t('common.header.logoJa')}
              </span>
              <span className={`inline whitespace-nowrap text-[8px] tracking-[0.1em] md:text-[12px] md:tracking-[0.2em] transition-colors duration-300 ${
                scrolled ? 'text-accent-400' : 'text-accent-400'
              }`}>
                {t('common.header.logoEn')}
              </span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-0" onMouseLeave={handleMenuLeave}>
            {navItems.map((item, index) => (
              <div
                key={index}
                className="relative"
                onMouseEnter={() => handleMenuEnter(index)}
              >
                <Link
                  to={navHref(item.href)}
                  className={`px-4 py-7 text-[15px] tracking-[0.06em] whitespace-nowrap transition-colors duration-300 flex items-center gap-1 ${
                    (item.label === 'お問い合わせ' && (location.pathname.startsWith('/contact') || location.pathname.startsWith('/en/contact') || location.pathname.startsWith('/brochure') || location.pathname.startsWith('/en/brochure')))
                      ? 'text-accent-500'
                      : (item.href === '/' ? (location.pathname === '/' || location.pathname === '/en') : (location.pathname.startsWith(item.href) || location.pathname.startsWith('/en' + item.href)))
                        ? 'text-accent-500'
                        : item.subItems.some(s => s.href === location.pathname || location.pathname === '/en' + s.href)
                          ? 'text-accent-500'
                          : scrolled
                            ? 'text-foreground-200 hover:text-accent-500'
                            : 'text-foreground-100 hover:text-accent-400'
                  }`}
                >
                  {t(NAV_KEY_MAP[item.label] ?? item.label)}
                </Link>

                {/* Mega Menu Dropdown */}
                {item.subItems.length > 0 && activeMenu === index && (
                  <div
                    className="absolute top-full left-1/2 -translate-x-1/2 bg-primary-900 border border-white/10 min-w-[220px] py-3 px-1 shadow-2xl"
                    onMouseEnter={() => handleMenuEnter(index)}
                  >
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[8px] border-b-white/10"></div>
                    {item.subItems.map((sub, subIdx) => (
                      <Link
                        key={subIdx}
                        to={navHref(sub.href)}
                        className="block px-5 py-2.5 text-[15px] text-foreground-300 hover:text-accent-400 hover:bg-white/[0.03] transition-all duration-200 whitespace-nowrap tracking-[0.04em]"
                      >
                        {t(SUB_KEY_MAP[sub.label] ?? sub.label)}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* CTA Button */}
            <Link
              to={navHref('/brochure')}
              className="ml-6 px-6 py-2.5 border border-accent-500/60 text-accent-400 text-[14px] tracking-[0.08em] whitespace-nowrap hover:bg-accent-500/10 hover:border-accent-400 transition-all duration-300"
            >
              {t('common.header.ctaBrochure')}
            </Link>
          </nav>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden w-11 h-11 flex items-center justify-center text-foreground-100"
            aria-label={t('common.header.menuAriaLabel')}
          >
            <i className={`text-xl transition-transform duration-300 ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-400 bg-primary-900 shadow-2xl ${
          mobileOpen ? 'max-h-[80vh] overflow-y-auto' : 'max-h-0'
        }`}
      >
        <nav className="px-6 py-4 flex flex-col">
          {navItems.map((item, index) => (
            <div key={index} className="border-b border-white/8 last:border-b-0">
              {item.subItems.length > 0 ? (
                <>
                  <button
                    onClick={() => toggleMobileSub(index)}
                    className="w-full flex items-center justify-between py-4 text-[16px] text-foreground-200 tracking-[0.06em]"
                  >
                    {t(NAV_KEY_MAP[item.label] ?? item.label)}
                    <i
                      className={`ri-arrow-down-s-line transition-transform duration-300 text-sm ${
                        expandedMobile === index ? 'rotate-180 text-accent-400' : 'text-foreground-400'
                      }`}
                    ></i>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      expandedMobile === index ? 'max-h-[300px] pb-3' : 'max-h-0'
                    }`}
                  >
                    {item.subItems.map((sub, subIdx) => (
                      <Link
                        key={subIdx}
                        to={navHref(sub.href)}
                        onClick={() => setMobileOpen(false)}
                        className="block pl-5 py-2.5 text-[15px] text-foreground-400 hover:text-accent-400 transition-colors tracking-[0.04em]"
                      >
                        {t(SUB_KEY_MAP[sub.label] ?? sub.label)}
                      </Link>
                    ))}
                  </div>
                </>
              ) : (
                <Link
                  to={navHref(item.href)}
                  onClick={() => setMobileOpen(false)}
                  className="block py-4 text-[16px] text-foreground-200 tracking-[0.06em] hover:text-accent-400 transition-colors"
                >
                  {t(NAV_KEY_MAP[item.label] ?? item.label)}
                </Link>
              )}
            </div>
          ))}
          <Link
            to={navHref('/brochure')}
            onClick={() => setMobileOpen(false)}
            className="mt-5 px-6 py-3 border border-accent-500/60 text-accent-400 text-[15px] tracking-[0.08em] text-center hover:bg-accent-500/10 transition-all duration-300"
          >
            {t('common.header.ctaBrochure')}
          </Link>

          {/* Mobile utility links (parity with PC Utility Bar: news / sitemap) */}
          <div className="mt-5 flex items-center justify-center gap-6 text-[13px] tracking-wider">
            <Link
              to={navHref('/news')}
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-util-news"
              className="min-h-[44px] flex items-center text-foreground-300 hover:text-accent-500 transition-colors duration-300 whitespace-nowrap"
            >
              {t('common.header.news')}
            </Link>
            <span className="text-foreground-500/40">|</span>
            <Link
              to={navHref('/sitemap')}
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-util-sitemap"
              className="min-h-[44px] flex items-center text-foreground-300 hover:text-accent-500 transition-colors duration-300 whitespace-nowrap"
            >
              {t('common.header.sitemap')}
            </Link>
          </div>

          {/* Mobile language toggle (JP/EN single segmented switch) */}
          <div className="mt-5 pt-4 border-t border-white/8 flex justify-center">
            <div className="inline-flex items-center border border-white/15 rounded-full overflow-hidden">
              <Link
                to={jaPath}
                onClick={() => setMobileOpen(false)}
                aria-current={lang === 'ja' ? 'true' : undefined}
                data-testid="mobile-lang-toggle-ja"
                className={`min-w-[44px] min-h-[44px] px-5 flex items-center justify-center text-[14px] tracking-[0.08em] transition-colors duration-300 ${
                  lang === 'ja'
                    ? 'bg-accent-500/15 text-accent-400'
                    : 'text-foreground-400 hover:text-accent-500'
                }`}
              >
                JP
              </Link>
              <Link
                to={enPath}
                onClick={() => setMobileOpen(false)}
                aria-current={lang === 'en' ? 'true' : undefined}
                data-testid="mobile-lang-toggle-en"
                className={`min-w-[44px] min-h-[44px] px-5 flex items-center justify-center text-[14px] tracking-[0.08em] transition-colors duration-300 border-l border-white/15 ${
                  lang === 'en'
                    ? 'bg-accent-500/15 text-accent-400'
                    : 'text-foreground-400 hover:text-accent-500'
                }`}
              >
                EN
              </Link>
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
}
